import { ChangeDetectionStrategy, Component, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SelectValueSyncDirective } from '../../../../../../../shared/directives/select-value-sync.directive';
import { WorkflowNodeConfigBase } from './node-config-base';

/**
 * ACTION → Integración y código: NATIVE_INTEGRATION_ACTION y CODE_SNIPPET.
 * Portado 1:1 del panel viejo (incluye los avisos de que ambas se registran como SKIPPED).
 */
@Component({
  selector: 'app-wf-code-integration-action-config',
  standalone: true,
  imports: [CommonModule, SelectValueSyncDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @switch (subtype()) {
      @case ('NATIVE_INTEGRATION_ACTION') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Proveedor</label>
            <input type="text" [class]="fieldClass" [value]="str('provider', 'GOOGLE_SHEETS')" [disabled]="readOnly()"
                   (input)="patchConfig({ provider: inputValue($event) })" />
          </div>
          <div>
            <label [class]="labelClass">Operación</label>
            <input type="text" [class]="fieldClass" [value]="str('operation', 'APPEND_ROW')" [disabled]="readOnly()"
                   (input)="patchConfig({ operation: inputValue($event) })" />
          </div>
          <div>
            <label [class]="labelClass">Conexión (id, opcional)</label>
            <input type="number" [class]="fieldClass" [value]="str('connectionId')" [disabled]="readOnly()"
                   (change)="patchConfig({ connectionId: numberOrUndefined(inputValue($event)) })" />
          </div>
          <div>
            <label [class]="labelClass">Parámetros (JSON)</label>
            <textarea rows="6" [class]="jsonAreaClass" [value]="jsonOf('params')" [disabled]="readOnly()"
                      (change)="setJson('params', inputValue($event))"></textarea>
          </div>
          <div class="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-500/30 dark:bg-amber-500/10">
            <p class="text-[11px] text-amber-700 dark:text-amber-400">
              La integración nativa aún no está configurada. Se registrará como SKIPPED.
            </p>
          </div>
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @case ('CODE_SNIPPET') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Lenguaje</label>
            <select [class]="fieldClass" [value]="str('language', 'JAVASCRIPT')" [disabled]="readOnly()"
                    (change)="patchConfig({ language: inputValue($event) })">
              <option value="JAVASCRIPT">JavaScript</option>
              <option value="PYTHON">Python</option>
            </select>
          </div>
          <div>
            <label [class]="labelClass">Código</label>
            <textarea rows="8" [class]="jsonAreaClass" [value]="str('code')" [disabled]="readOnly()"
                      (input)="patchConfig({ code: inputValue($event) })"></textarea>
          </div>
          <div>
            <label [class]="labelClass">Tiempo máximo de espera (segundos)</label>
            <input type="number" min="1" [class]="fieldClass" [value]="num('timeoutSeconds', 5)" [disabled]="readOnly()"
                   (change)="patchConfig({ timeoutSeconds: numberValue($event) })" />
          </div>
          <div class="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-500/30 dark:bg-amber-500/10">
            <p class="text-[11px] text-amber-700 dark:text-amber-400">
              CODE_SNIPPET está deshabilitado hasta tener un sandbox seguro. Se registrará como SKIPPED.
            </p>
          </div>
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }
    }

    <ng-template #continueOnError>
      <label class="flex items-center gap-2 border-t border-gray-100 pt-3 text-xs text-gray-700 dark:border-gray-800 dark:text-gray-300">
        <input type="checkbox" class="rounded border-gray-300" [checked]="bool('continueOnError', true)" [disabled]="readOnly()"
               (change)="patchConfig({ continueOnError: checkboxValue($event) })" />
        Continuar el flujo si esta acción falla
      </label>
    </ng-template>
  `
})
export class WorkflowCodeIntegrationActionConfigComponent extends WorkflowNodeConfigBase {
  protected readonly subtype = computed(() => this.node().nodeSubtype ?? '');
  protected readonly jsonAreaClass =
    'w-full rounded-lg border border-gray-300 px-2 py-1.5 font-mono text-[11px] text-gray-800 focus:border-brand-500 focus:outline-none disabled:opacity-60 dark:border-gray-700 dark:bg-gray-950 dark:text-white/90';

  protected numberOrUndefined(value: string): number | undefined {
    const n = Number(value);
    return value.trim() && Number.isFinite(n) ? n : undefined;
  }
}
